function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import Chart from "../../components/chart";
import { ChartLoader } from "../../components/loaders";
import publishSearch from "../../functions/publish-search";
import publishEvent from "../../functions/publish-event";
import { hasTenantFeature } from "../../functions/tenant-features";
import { getGlobalRange } from "../../functions/period-store";
import { createAudience, addMembers } from "../audiences/api";
import { fetchForecast, fetchV1, num, usdShort, usd0, shortDate, monthLabel, performanceOf, recTopic, plain, Wait } from "./forecast-common";
import { decodeEntities } from "../stores/common";

// Days an AI review stays fresh enough to show its one-word verdict as a header badge.
const REVIEW_FRESH_DAYS = 60;

/*
 * A product forecast (board "5 · A product forecast (sales + interest)", approved 2026-10-09).
 *
 * The old page awaited six calls one after another (nothing showed until the slowest
 * landed), then mounted four Kendo grids at once, and its base class re-ran ALL six calls
 * and re-mounted every grid on each pager or sort click (componentDidUpdate → fetchData).
 *
 * This page: every section loads on its own (no awaits chained), renders plain tables
 * capped at 10 rows with "Show all", and nothing re-fetches on interaction. Sources:
 *   summary          /forecast/product-forecast/{id}/summary   (header + interest tiles)
 *   recommendations  /forecast/product-forecast/recommendation/{id}
 *   interest curve   /forecast/product-forecast/{id}            (views + people by month)
 *   cities / areas   /forecast/product-forecast/location/{id}/city · place/{id}/name
 *   people           /forecast/product-forecast/contact/{id}
 *   sales outlook    /sales/forecast/year?product=&months=12    (the product page's "What's coming")
 *   similar sellers  /sales/copurchase?product=                 (bought by the same customers)
 *   stock check      /inventory/products/{id}/trend             (inventory tenants only)
 */

const CORAL = "#ff6358";
const TEAL = "#14b8a6";
const SLATE = "#64748b";
const LIMIT = 10;
function minutes(sec) {
  const s = Number(sec) || 0;
  if (s <= 0) return "under a minute";
  if (s < 60) return `${Math.round(s)} sec`;
  return `${Math.round(s / 60)} min`;
}
function items(d) {
  if (Array.isArray(d)) return d;
  return d && d.items || [];
}
class MainComponent extends React.Component {
  constructor(props) {
    super(props);
    // ── Save as audience (existing audience rail: create a static list, add the people by email) ──
    _defineProperty(this, "saveAudience", async () => {
      const name = this.state.saveName.trim();
      if (!name) {
        this.setState({
          saveErr: "Give the audience a name."
        });
        return;
      }
      const emails = Array.from(new Set((this.state.people || []).map(p => String(p.email || "").split(",")[0].trim().toLowerCase()).filter(e => e.indexOf("@") > 0)));
      if (!emails.length) {
        this.setState({
          saveErr: "Nobody here has an email address yet."
        });
        return;
      }
      this.setState({
        saving: true,
        saveErr: null
      });
      try {
        const created = await createAudience({
          name,
          kind: "static",
          description: `People who looked at ${this.info().title} (from Forecasts)`
        });
        const aid = created && created.id != null ? String(created.id) : null;
        if (!aid) throw new Error("The audience was not created.");
        await addMembers(aid, {
          emails
        });
        if (this._alive) this.setState({
          saving: false,
          save: null,
          saveMsg: {
            id: aid,
            name,
            n: emails.length
          }
        });
      } catch (e) {
        const msg = e && e.status === 409 ? `There is already an audience called “${name}”. Pick another name.` : e && e.message || "Could not save the audience.";
        if (this._alive) this.setState({
          saving: false,
          saveErr: msg
        });
      }
    });
    this.state = this.blank();
    this._alive = true;
  }
  blank() {
    const st = this.props.location && this.props.location.state;
    return {
      row: st && st.row || null,
      // list row handed over by the Forecasts page (instant header)
      summary: null,
      summaryError: false,
      recs: null,
      openRec: null,
      interest: null,
      sales: null,
      cities: null,
      places: null,
      whereTab: "cities",
      whereAll: false,
      people: null,
      peopleAll: false,
      similar: null,
      stock: null,
      save: null,
      saveName: "",
      saving: false,
      saveMsg: null,
      saveErr: null
    };
  }
  componentDidMount() {
    publishSearch("Forecasts");
    publishEvent("reset-search", {});
    this.load();
  }
  componentDidUpdate(prev) {
    if ((prev.params && prev.params.id) !== (this.props.params && this.props.params.id)) {
      this.setState(this.blank(), () => this.load());
    }
  }
  componentWillUnmount() {
    this._alive = false;
  }
  id() {
    return String(this.props.params && this.props.params.id || "");
  }

  // Run one section's request; it lands whenever it lands, independent of the rest.
  section(promise, onOk, onFail) {
    const id = this.id();
    promise.then(d => {
      if (this._alive && id === this.id()) this.setState(onOk(d));
    }).catch(() => {
      if (this._alive && id === this.id()) this.setState(onFail ? onFail() : {});
    });
  }
  load() {
    this._pid = null;
    const id = encodeURIComponent(this.id());
    const base = "/forecast/product-forecast";

    // Header facts; product id (sku) unlocks the sales-side sections.
    const row = this.state.row;
    if (row && row.productId) this.loadProductSections(row.productId);
    fetchForecast(`${base}/${id}/summary`).catch(() => fetchForecast(`${base}/${id}/detail`).then(d => d ? {
      productId: d.id,
      title: d.title,
      imageLink: d.imageLink,
      link: d.link
    } : null)).then(s => {
      if (!this._alive || this.id() !== decodeURIComponent(id)) return;
      if (!s) {
        this.setState({
          summaryError: true
        });
        return;
      }
      this.setState({
        summary: s
      });
      if (!(row && row.productId) && s.productId) this.loadProductSections(s.productId);
    }).catch(() => {
      if (this._alive) this.setState({
        summaryError: true
      });
    });
    this.section(fetchForecast(`${base}/recommendation/${id}`), d => ({
      recs: items(d)
    }), () => ({
      recs: []
    }));
    this.section(fetchForecast(`${base}/${id}?$top=60`), d => ({
      interest: items(d)
    }), () => ({
      interest: []
    }));
    this.section(fetchForecast(`${base}/location/${id}/city?$top=200&$skip=0&$orderBy=City`), d => ({
      cities: items(d)
    }), () => ({
      cities: []
    }));
    this.section(fetchForecast(`${base}/place/${id}/name?$top=100&$skip=0&$orderBy=Name`), d => ({
      places: items(d)
    }), () => ({
      places: []
    }));
    this.section(fetchForecast(`${base}/contact/${id}?$top=1000`), d => ({
      people: items(d)
    }), () => ({
      people: []
    }));
  }
  loadProductSections(productId) {
    if (this._pid === productId) return;
    this._pid = productId;
    const pid = encodeURIComponent(productId);
    this.section(fetchV1(`/sales/forecast/year?product=${pid}&months=12`), d => ({
      sales: Array.isArray(d) ? d : items(d)
    }), () => ({
      sales: []
    }));
    this.section(fetchV1(`/sales/copurchase?product=${pid}&limit=6`), d => ({
      similar: items(d)
    }), () => ({
      similar: []
    }));
    if (hasTenantFeature("inventory")) {
      this.section(fetchV1(`/inventory/products/${pid}/trend?range=this_year&grain=day`), d => ({
        stock: d || false
      }), () => ({
        stock: false
      }));
    } else {
      this.setState({
        stock: false
      });
    }
  }

  // ── derived ──
  info() {
    const s = this.state.summary || {};
    const r = this.state.row || {};
    return {
      productId: s.productId || r.productId || null,
      title: s.title || r.title || "",
      imageLink: s.imageLink || r.imageLink || null,
      link: s.link || r.link || null,
      category: decodeEntities(s.category || r.category) || null,
      sku: r.sku || s.productId || r.productId || null,
      modifiedDate: s.modifiedDate || r.modifiedDate || null,
      views: s.views != null ? s.views : r.views,
      people: s.uniqueUsers != null ? s.uniqueUsers : r.uniqueUsers,
      contacts: s.contacts != null ? s.contacts : r.contacts,
      cities: s.cities,
      topCity: s.topCity,
      actual: this.actual(),
      ready: !!(this.state.summary || this.state.row)
    };
  }

  // REAL product page visits, trailing 12 months (product_stats via the summary / overview
  // endpoints). undefined = still loading; null = no data for this product.
  actual() {
    const s = this.state.summary;
    const r = this.state.row;
    if (s && s.actual) return {
      start: s.actual.start,
      views: s.actual.views || [],
      totalViews: Number(s.actual.totalViews) || 0,
      totalVisits: Number(s.actual.totalSessions) || 0
    };
    if (r && r.actualStart) return {
      start: r.actualStart,
      views: r.actualMonthly || [],
      totalViews: Number(r.actualViews) || 0,
      totalVisits: Number(r.actualSessions) || 0
    };
    if (s || this.state.summaryError) return null;
    return undefined;
  }
  salesForecast() {
    const rows = this.state.sales;
    if (!rows) return undefined;
    const f = rows.filter(r => r.isForecast);
    if (!f.length) return null;
    const sum = k => f.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    return {
      total: sum("totalAmount"),
      low: sum("totalAmountLower"),
      high: sum("totalAmountUpper"),
      units: sum("quantity"),
      months: f.length,
      perMonthUnits: sum("quantity") / f.length
    };
  }
  salesChart() {
    const rows = this.state.sales;
    if (!rows || rows.length < 2 || !rows.some(r => r.isForecast)) return null;
    const data = [["Period", "Sold", "Expected", {
      role: "interval"
    }, {
      role: "interval"
    }]];
    const actual = rows.filter(r => !r.isForecast).slice(-6);
    const future = rows.filter(r => r.isForecast);
    actual.concat(future).forEach(r => {
      const amt = Math.round(Number(r.totalAmount) || 0);
      if (r.isForecast) data.push([monthLabel(r.period), null, amt, Math.round(Number(r.totalAmountLower) || 0), Math.round(Number(r.totalAmountUpper) || 0)]);else data.push([monthLabel(r.period), amt, null, null, null]);
    });
    return data;
  }

  // Stored interest forecast rows, only when the last forecast run produced real (non-zero) numbers.
  expectedRows() {
    const rows = (this.state.interest || []).slice().sort((a, b) => String(a.date).localeCompare(String(b.date)));
    return rows.some(r => Number(r.views) > 0) ? rows : [];
  }

  // Past 12 months of actual views (bars) + the expected line where the stored forecast has numbers.
  interestChart() {
    const a = this.actual();
    const byMonth = {};
    if (a && a.start) {
      const [y, m] = a.start.split("-").map(Number);
      (a.views || []).forEach((v, k) => {
        const d = new Date(y, m - 1 + k, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        byMonth[key] = {
          a: Math.max(0, Math.round(Number(v) || 0)),
          e: null
        };
      });
    }
    const expected = this.expectedRows();
    expected.forEach(r => {
      const key = String(r.date).slice(0, 7);
      byMonth[key] = byMonth[key] || {
        a: null,
        e: null
      };
      byMonth[key].e = Math.max(0, Math.round(Number(r.views) || 0));
    });
    const hasActual = !!(a && a.totalViews > 0);
    if (!hasActual && !expected.length) return null;
    const keys = Object.keys(byMonth).sort();
    const withExpected = expected.length > 0;
    const data = [withExpected ? ["Month", "Viewed", "Expected"] : ["Month", "Viewed"]];
    keys.forEach(k => data.push(withExpected ? [monthLabel(k), byMonth[k].a, byMonth[k].e] : [monthLabel(k), byMonth[k].a]));
    return data;
  }
  interestTrend() {
    const rows = this.expectedRows();
    if (rows.length < 2) return null;
    const first = Number(rows[0].views) || 0,
      last = Number(rows[rows.length - 1].views) || 0;
    if (first <= 0) return last > 0 ? "expected to grow" : null;
    const r = last / first;
    return r >= 1.1 ? "expected to grow" : r <= 0.9 ? "expected to slow" : "expected to hold steady";
  }

  // When the AI review was written (recommendation rows carry createdDate since 2026-10-12; older APIs don't).
  reviewDate() {
    const ds = (this.state.recs || []).map(r => r.createdDate || r.CreatedDate).filter(Boolean).map(d => new Date(d)).filter(d => !isNaN(d.getTime()));
    return ds.length ? new Date(Math.max(...ds.map(d => d.getTime()))) : null;
  }

  // A review older than REVIEW_FRESH_DAYS judged numbers that have since moved (a Jul 21 "below average"
  // verdict sat on the #1 forecast product in October), so its one-word verdict is not shown as a badge.
  reviewStale() {
    const d = this.reviewDate();
    return !!d && (Date.now() - d.getTime()) / 864e5 > REVIEW_FRESH_DAYS;
  }
  score() {
    if (this.reviewStale()) return null;
    const s = (this.state.recs || []).find(r => String(r.category).toLowerCase() === "score");
    return s ? performanceOf(s.answer) : null;
  }
  productPagePath(pid) {
    return `/Products/${getGlobalRange() || "this_year"}/product/${encodeURIComponent(pid)}`;
  }
  // ── sections ──
  renderHeader(i) {
    const score = this.score();
    return /*#__PURE__*/React.createElement("div", {
      className: "fc-hdr"
    }, i.imageLink ? /*#__PURE__*/React.createElement("img", {
      className: "fc-hdr-img",
      src: i.imageLink,
      alt: "",
      onError: e => {
        e.currentTarget.style.visibility = "hidden";
      }
    }) : /*#__PURE__*/React.createElement("span", {
      className: "fc-hdr-img"
    }), /*#__PURE__*/React.createElement("div", {
      className: "fc-hdr-main"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-crumbs"
    }, /*#__PURE__*/React.createElement(Link, {
      to: "/Forecast/Product"
    }, "Forecasts"), " \xB7 Your products"), /*#__PURE__*/React.createElement("h1", null, i.title || (this.state.summaryError ? "Forecast not found" : " ")), /*#__PURE__*/React.createElement("span", {
      className: "fc-sub"
    }, [i.sku, i.category, i.modifiedDate ? `forecast run ${shortDate(i.modifiedDate)}, visits current` : null].filter(Boolean).join(" · "))), /*#__PURE__*/React.createElement("span", {
      className: "fc-hdr-actions"
    }, score ? /*#__PURE__*/React.createElement("span", {
      className: `fc-score ${score.tone}`,
      title: "From the AI review of this product's sales and website activity"
    }, score.label) : null, i.link && i.link !== "#" ? /*#__PURE__*/React.createElement("a", {
      className: "fc-btn",
      href: i.link,
      target: "_blank",
      rel: "noopener noreferrer"
    }, "View on website") : null, i.productId ? /*#__PURE__*/React.createElement(Link, {
      className: "fc-btn",
      to: {
        pathname: this.productPagePath(i.productId),
        state: {
          title: i.title
        }
      }
    }, "Open product page") : null));
  }
  renderTiles(i) {
    const fc = this.salesForecast();
    const wait = /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-wait"
    });
    const trend = this.interestTrend();
    const top = i.topCity ? [i.topCity.city, i.topCity.region].filter(Boolean).join(", ") : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "fc-tiles"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-tile"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-l"
    }, "Next 12 months"), /*#__PURE__*/React.createElement("b", {
      className: "fc-tile-v"
    }, fc === undefined ? wait : fc ? usdShort(fc.total) : "None"), /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-s"
    }, fc === undefined ? "" : fc ? fc.high > fc.low ? `likely ${usdShort(Math.max(0, fc.low))} to ${usdShort(fc.high)}` : `about ${num(Math.round(fc.units))} units` : "no sales to forecast from yet")), /*#__PURE__*/React.createElement("div", {
      className: "fc-tile"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-l"
    }, "Website views"), /*#__PURE__*/React.createElement("b", {
      className: "fc-tile-v"
    }, i.actual === undefined ? wait : num(i.actual ? i.actual.totalViews : i.views)), /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-s"
    }, i.actual === undefined ? "" : [i.actual ? i.actual.totalViews > 0 ? `${num(i.actual.totalVisits)} visits · last 12 months` : "no visits to this product's page in the last 12 months" : null, Number(i.views) > 0 ? `${num(i.views)} expected next 12 months` : null, Number(i.views) > 0 ? trend : null].filter(Boolean).join(" · "))), /*#__PURE__*/React.createElement("div", {
      className: "fc-tile"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-l"
    }, "People you can reach"), /*#__PURE__*/React.createElement("b", {
      className: "fc-tile-v"
    }, i.ready ? num(i.contacts) : wait), /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-s"
    }, "known contacts who looked at it")), /*#__PURE__*/React.createElement("div", {
      className: "fc-tile"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-l"
    }, "Where the interest is"), /*#__PURE__*/React.createElement("b", {
      className: "fc-tile-v"
    }, i.cities != null ? `${num(i.cities)} ${i.cities === 1 ? "city" : "cities"}` : this.state.cities ? `${num(this.state.cities.length)} cities` : wait), /*#__PURE__*/React.createElement("span", {
      className: "fc-tile-s"
    }, top ? `top: ${top}` : "")));
  }
  renderCharts() {
    const sales = this.salesChart();
    const fc = this.salesForecast();
    const interest = this.interestChart();
    return /*#__PURE__*/React.createElement("div", {
      className: "fc-two"
    }, /*#__PURE__*/React.createElement("section", {
      className: "fc-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Sales outlook"), /*#__PURE__*/React.createElement("span", {
      className: "fc-card-hint"
    }, "shaded band is the likely range")), this.state.sales == null ? /*#__PURE__*/React.createElement(Wait, null) : sales ? /*#__PURE__*/React.createElement("div", {
      className: "fc-chart"
    }, /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "260px",
      chartType: "ComboChart",
      loader: /*#__PURE__*/React.createElement(ChartLoader, null),
      data: sales,
      options: {
        seriesType: "bars",
        series: {
          0: {
            color: CORAL,
            tooltipFormat: "{0:c0}"
          },
          1: {
            type: "line",
            color: TEAL,
            lineDashStyle: [6, 3],
            tooltipFormat: "{0:c0}"
          }
        },
        vAxis: {
          format: "short"
        },
        legend: {
          position: "top"
        },
        pointSize: 0,
        backgroundColor: "transparent"
      }
    })) : /*#__PURE__*/React.createElement("div", {
      className: "fc-empty"
    }, "Not enough sales yet to forecast this product."), fc ? /*#__PURE__*/React.createElement("div", {
      className: "fc-note"
    }, "About ", usd0(fc.total / fc.months), " a month expected, based on this product's recent sales and the time of year. Same forecast as the product page.") : null), /*#__PURE__*/React.createElement("section", {
      className: "fc-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Interest outlook"), /*#__PURE__*/React.createElement("span", {
      className: "fc-card-hint"
    }, this.expectedRows().length ? "product page views by month, last 12 months and expected" : "product page views by month, last 12 months")), this.actual() === undefined || this.state.interest == null ? /*#__PURE__*/React.createElement(Wait, null) : interest ? /*#__PURE__*/React.createElement("div", {
      className: "fc-chart"
    }, /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "260px",
      chartType: "ComboChart",
      loader: /*#__PURE__*/React.createElement(ChartLoader, null),
      data: interest,
      options: {
        seriesType: "bars",
        series: {
          0: {
            color: CORAL,
            tooltipFormat: "{0:n0}"
          },
          1: {
            type: "line",
            color: TEAL,
            lineDashStyle: [6, 3],
            tooltipFormat: "{0:n0}"
          }
        },
        vAxis: {
          format: "short"
        },
        legend: {
          position: "top"
        },
        pointSize: 0,
        backgroundColor: "transparent"
      }
    })) : /*#__PURE__*/React.createElement("div", {
      className: "fc-empty"
    }, "No visits to this product's page in the last 12 months.")));
  }
  renderRecs() {
    const recs = this.state.recs;
    const {
      openRec
    } = this.state;
    // Group by topic; the score answer leads with the plain verdict in the header chip, its detail stays here.
    const groups = [];
    (recs || []).forEach(r => {
      const k = String(r.category || "other").toLowerCase();
      let g = groups.find(x => x.k === k);
      if (!g) {
        g = {
          k,
          label: recTopic(k),
          items: []
        };
        groups.push(g);
      }
      g.items.push(r);
    });
    const order = ["marketing", "location", "pricing", "stock", "score"];
    groups.sort((a, b) => (order.indexOf(a.k) < 0 ? 99 : order.indexOf(a.k)) - (order.indexOf(b.k) < 0 ? 99 : order.indexOf(b.k)));
    return /*#__PURE__*/React.createElement("section", {
      className: "fc-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "What to do next"), /*#__PURE__*/React.createElement("span", {
      className: "fc-card-hint"
    }, this.reviewDate() ? `suggestions from an AI review of this product on ${shortDate(this.reviewDate())}${this.reviewStale() ? ", more than two months ago, so check them against the numbers above" : ""}` : "suggestions from an AI review of this product", " \xB7 click one to see the question it answers")), recs == null ? /*#__PURE__*/React.createElement(Wait, {
      short: true
    }) : !groups.length ? /*#__PURE__*/React.createElement("div", {
      className: "fc-empty"
    }, "No suggestions for this product yet.") : /*#__PURE__*/React.createElement("div", {
      className: "fc-recs"
    }, groups.map(g => g.items.map((r, idx) => {
      const key = `${g.k}-${idx}`;
      const open = openRec === key;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: key,
        className: `fc-rec${open ? " open" : ""}`,
        title: open ? "" : plain(r.question),
        onClick: () => this.setState({
          openRec: open ? null : key
        }),
        "aria-expanded": open
      }, /*#__PURE__*/React.createElement("span", {
        className: "fc-rec-l"
      }, g.label), /*#__PURE__*/React.createElement("span", {
        className: "fc-rec-a"
      }, plain(r.answer)), open && r.question ? /*#__PURE__*/React.createElement("span", {
        className: "fc-rec-q"
      }, "Question asked: ", plain(r.question)) : /*#__PURE__*/React.createElement("span", {
        className: "fc-rec-more"
      }, "Read more"));
    }))));
  }
  renderWhere() {
    const {
      cities,
      places,
      whereTab,
      whereAll
    } = this.state;
    const list = whereTab === "cities" ? cities : places;
    const shown = list ? whereAll ? list : list.slice(0, LIMIT) : null;
    return /*#__PURE__*/React.createElement("section", {
      className: "fc-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-card-h"
    }, /*#__PURE__*/React.createElement("b", {
      className: "fc-grow"
    }, "Where people are looking from"), /*#__PURE__*/React.createElement("span", {
      className: "fc-chips"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `fc-chip${whereTab === "cities" ? " on" : ""}`,
      onClick: () => this.setState({
        whereTab: "cities",
        whereAll: false
      })
    }, "Cities", cities ? /*#__PURE__*/React.createElement("span", {
      className: "fc-chip-n"
    }, num(cities.length)) : null), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `fc-chip${whereTab === "places" ? " on" : ""}`,
      onClick: () => this.setState({
        whereTab: "places",
        whereAll: false
      })
    }, "Areas", places ? /*#__PURE__*/React.createElement("span", {
      className: "fc-chip-n"
    }, num(places.length)) : null))), !shown ? /*#__PURE__*/React.createElement(Wait, null) : !shown.length ? /*#__PURE__*/React.createElement("div", {
      className: "fc-empty"
    }, whereTab === "cities" ? "No cities yet." : "No named areas yet.") : whereTab === "cities" ? /*#__PURE__*/React.createElement("table", {
      className: "fc-tbl"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "City"), /*#__PURE__*/React.createElement("th", null, "State"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "ZIP"))), /*#__PURE__*/React.createElement("tbody", null, shown.map((c, k) => /*#__PURE__*/React.createElement("tr", {
      key: `${c.city}-${c.postalCode}-${k}`
    }, /*#__PURE__*/React.createElement("td", null, c.city || "Unknown"), /*#__PURE__*/React.createElement("td", null, [c.regionCode, c.countryCode && c.countryCode !== "US" ? c.countryCode : null].filter(Boolean).join(", ")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, c.postalCode))))) : /*#__PURE__*/React.createElement("table", {
      className: "fc-tbl"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Area"), /*#__PURE__*/React.createElement("th", null, "ZIP codes"))), /*#__PURE__*/React.createElement("tbody", null, shown.map((p, k) => /*#__PURE__*/React.createElement("tr", {
      key: `${p.name}-${k}`
    }, /*#__PURE__*/React.createElement("td", null, p.name), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
      className: "zips",
      title: String(p.postalCodes || "")
    }, String(p.postalCodes || "").split(",").map(z => z.trim()).filter(Boolean).join(", "))))))), /*#__PURE__*/React.createElement("div", {
      className: "fc-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-foot-note"
    }, whereTab === "cities" ? "Cities where people looked at this product most." : "Named areas with their ZIP codes, ready to use as an ad audience."), list && list.length > LIMIT ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "fc-link",
      onClick: () => this.setState({
        whereAll: !whereAll
      })
    }, whereAll ? "Show fewer" : `Show all ${num(list.length)}`) : null));
  }
  renderPeople() {
    const {
      people,
      peopleAll,
      save,
      saveName,
      saving,
      saveMsg,
      saveErr
    } = this.state;
    const sorted = people ? people.slice().sort((a, b) => (Number(b.sessions) || 0) - (Number(a.sessions) || 0)) : null;
    const shown = sorted ? peopleAll ? sorted : sorted.slice(0, LIMIT) : null;
    const withEmail = sorted ? sorted.filter(p => String(p.email || "").indexOf("@") > 0).length : 0;
    return /*#__PURE__*/React.createElement("section", {
      className: "fc-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-card-h"
    }, /*#__PURE__*/React.createElement("b", {
      className: "fc-grow"
    }, "People who looked at it"), withEmail > 0 && !save ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "fc-link",
      onClick: () => this.setState({
        save: true,
        saveName: `Looked at ${this.info().title}`.slice(0, 80),
        saveErr: null,
        saveMsg: null
      })
    }, "Save as audience") : null), save ? /*#__PURE__*/React.createElement("div", {
      className: "fc-save"
    }, /*#__PURE__*/React.createElement("input", {
      className: "fc-search",
      value: saveName,
      onChange: e => this.setState({
        saveName: e.target.value
      }),
      "aria-label": "Audience name",
      placeholder: "Audience name"
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "fc-btn primary",
      disabled: saving,
      onClick: this.saveAudience
    }, saving ? "Saving…" : `Save ${num(withEmail)} people`), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "fc-btn",
      disabled: saving,
      onClick: () => this.setState({
        save: null,
        saveErr: null
      })
    }, "Cancel"), saveErr ? /*#__PURE__*/React.createElement("span", {
      className: "fc-err"
    }, saveErr) : null) : null, saveMsg ? /*#__PURE__*/React.createElement("div", {
      className: "fc-save"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-ok"
    }, "Saved \u201C", saveMsg.name, "\u201D with ", num(saveMsg.n), " people."), /*#__PURE__*/React.createElement(Link, {
      className: "fc-link",
      to: `/Audiences/Detail/${saveMsg.id}/${getGlobalRange() || "this_year"}`
    }, "Open it")) : null, !shown ? /*#__PURE__*/React.createElement(Wait, null) : !shown.length ? /*#__PURE__*/React.createElement("div", {
      className: "fc-empty"
    }, "No known contacts have looked at this product yet.") : /*#__PURE__*/React.createElement("table", {
      className: "fc-tbl"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Person"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Visits to your website overall, not just this product"
    }, "Site visits"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Average time per visit"
    }, "Time per visit"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Ordered"))), /*#__PURE__*/React.createElement("tbody", null, shown.map(p => {
      const email = String(p.email || "").split(",")[0].trim().toLowerCase();
      // Contact names arrive in whatever case they were typed; show them in normal case.
      const name = String(p.name || "").trim().toLowerCase().replace(/(^|[\s'-])([a-z])/g, (m, a, b) => a + b.toUpperCase());
      return /*#__PURE__*/React.createElement("tr", {
        key: String(p.id),
        className: "click",
        onClick: () => this.props.history.push(`/Contacts/Detail/${String(p.id)}`),
        title: "Open this person"
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "who"
      }, /*#__PURE__*/React.createElement("b", null, name || email || "Unknown visitor"), name && email ? /*#__PURE__*/React.createElement("span", null, email) : null)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(p.sessions)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, minutes(p.avgSession)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, Number(p.conversions) > 0 ? "yes" : "no"));
    }))), /*#__PURE__*/React.createElement("div", {
      className: "fc-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-foot-note"
    }, "Phone and email open from the person's page."), sorted && sorted.length > LIMIT ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "fc-link",
      onClick: () => this.setState({
        peopleAll: !peopleAll
      })
    }, peopleAll ? "Show fewer" : `Show all ${num(sorted.length)}`) : null));
  }
  renderSimilar() {
    const sim = this.state.similar;
    if (sim && !sim.length) return null;
    const max = sim ? Math.max(...sim.map(s => Number(s.score) || 0), 0) : 0;
    return /*#__PURE__*/React.createElement("section", {
      className: "fc-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Products that sell like this one"), /*#__PURE__*/React.createElement("span", {
      className: "fc-card-hint"
    }, "bought by the same customers")), !sim ? /*#__PURE__*/React.createElement(Wait, {
      short: true
    }) : /*#__PURE__*/React.createElement("div", null, sim.map(s => /*#__PURE__*/React.createElement("div", {
      key: String(s.id || s.sku),
      className: "fc-sim",
      onClick: () => this.props.history.push({
        pathname: this.productPagePath(s.id || s.sku),
        state: {
          title: s.title
        }
      }),
      title: `Open ${s.title}`
    }, s.imageLink ? /*#__PURE__*/React.createElement("img", {
      src: s.imageLink,
      alt: "",
      loading: "lazy",
      onError: e => {
        e.currentTarget.style.visibility = "hidden";
      }
    }) : /*#__PURE__*/React.createElement("img", {
      alt: ""
    }), /*#__PURE__*/React.createElement("span", {
      className: "fc-sim-t"
    }, s.title || s.sku), /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, max > 0 && (Number(s.score) || 0) >= max * 0.75 ? "very similar" : "similar")))), /*#__PURE__*/React.createElement("div", {
      className: "fc-note"
    }, "Products the same customers also buy, from your sales history."));
  }
  renderStock() {
    const stock = this.state.stock;
    if (stock === false) return null;
    const inv = stock && Array.isArray(stock.inventory) ? stock.inventory : null;
    if (stock && (!inv || !inv.length)) return null;
    const latest = inv ? inv[inv.length - 1] : null;
    const available = latest ? Math.max(0, Number(latest.atp) || 0) : 0;
    const fc = this.salesForecast();
    let runsOut = null;
    if (latest) {
      if (available <= 0) runsOut = "Out of stock now";else if (fc && fc.perMonthUnits > 0) {
        const months = available / fc.perMonthUnits;
        if (months > 12) runsOut = "Not within a year";else {
          const d = new Date();
          d.setDate(d.getDate() + Math.round(months * 30.4));
          runsOut = d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined
          });
        }
      } else if (fc !== undefined) runsOut = "No sales expected";
    }
    return /*#__PURE__*/React.createElement("section", {
      className: "fc-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-card-h"
    }, /*#__PURE__*/React.createElement("b", null, "Stock check")), !latest ? /*#__PURE__*/React.createElement(Wait, {
      short: true
    }) : /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "fc-kv"
    }, /*#__PURE__*/React.createElement("span", null, "Available to sell now"), /*#__PURE__*/React.createElement("b", null, num(available), " ", available === 1 ? "unit" : "units")), /*#__PURE__*/React.createElement("div", {
      className: "fc-kv"
    }, /*#__PURE__*/React.createElement("span", null, "Runs out around"), /*#__PURE__*/React.createElement("b", null, runsOut || "…")), stock.nextAvailable ? /*#__PURE__*/React.createElement("div", {
      className: "fc-kv"
    }, /*#__PURE__*/React.createElement("span", null, "More arriving"), /*#__PURE__*/React.createElement("b", null, shortDate(stock.nextAvailable))) : null), /*#__PURE__*/React.createElement("div", {
      className: "fc-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "fc-foot-note"
    }, "At the expected pace of sales above."), /*#__PURE__*/React.createElement(Link, {
      className: "fc-link",
      to: "/Inventory"
    }, "Open in the inventory planner")));
  }
  render() {
    const i = this.info();
    const similar = this.renderSimilar();
    const stock = this.renderStock();
    return /*#__PURE__*/React.createElement("div", {
      className: "fc-view territory-view wa-view"
    }, this.renderHeader(i), this.state.summaryError && !i.ready ? /*#__PURE__*/React.createElement("section", {
      className: "fc-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "fc-empty"
    }, "This forecast could not be found. ", /*#__PURE__*/React.createElement(Link, {
      className: "fc-link",
      to: "/Forecast/Product"
    }, "Back to Forecasts"))) : /*#__PURE__*/React.createElement(React.Fragment, null, this.renderTiles(i), this.renderCharts(), this.renderRecs(), /*#__PURE__*/React.createElement("div", {
      className: "fc-two"
    }, this.renderWhere(), this.renderPeople()), similar || stock ? /*#__PURE__*/React.createElement("div", {
      className: similar && stock ? "fc-two" : ""
    }, similar, stock) : null));
  }
}
const RoutableComponent = withRouter(MainComponent);
export class ForecastProductDetailViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(RoutableComponent, {
      params: this.props.params
    });
  }
}
export default {
  ForecastProductDetailViewComponent
};